import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, Box, Card, CardContent, Typography, Divider, DialogActions } from '@mui/material'
import { useNotification } from '@/hooks/useNotification'
import { useSubmitDeleteUserMutation } from '@/@data'
import { useTranslator } from '@/hooks/useTranslator'
import { Button } from '@/@core/components/mui/Button'

interface EmployeeConfirmRequestDeleteDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  employee: any
  refetch?: () => void
}

export const EmployeeConfirmRequestDeleteDialog = ({
  open,
  onClose,
  employee,
  onSuccess,
  refetch
}: EmployeeConfirmRequestDeleteDialogProps) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const submitDeleteUserMutation = useSubmitDeleteUserMutation()
  const navigate = useNavigate()

  if (!employee) return null

  const handleDelete = () => {
    submitDeleteUserMutation.mutate(
      {
        id: employee.id,
        name: 'approve'
      },
      {
        onSuccess: () => {
          n.success(__('Delete request approved'))
          onSuccess?.()
          refetch?.()
          onClose()
          navigate('/cms/users/employee')
        },
        onError: error => {
          n.error(error?.message ? __(error.message) : __('Delete request failed'))
        }
      }
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      scroll='body'
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '650px', borderRadius: '32px' }
      }}
    >
      <DialogContent className='p-0 m-0' sx={{ padding: 0, borderRadius: '32px' }}>
        <DialogCloseButton onClick={onClose} disableRipple>
          <i className='tabler-x' />
        </DialogCloseButton>
        <Card>
          <CardContent>
            <Typography variant='h5' className='text-[#2F2B3DE5] mb-6'>
              {__('Confirm Request Delete Employee')}
            </Typography>

            <Box className='flex items-center justify-center gap-6'>
              <Box className='flex-1 rounded-2xl bg-[#3551F214] p-6 border border-[#3551F226]'>
                <Typography variant='h6' className='text-[#2F2B3DE5]'>
                  {__('Employee Information')}
                </Typography>
                <Divider className='my-4' />
                <Box className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employee Name')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.first_name || employee?.last_name
                        ? `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()
                        : '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employer')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.company?.name || employee?.company_name || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employee Code')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.employee_id || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Phone')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.phone_code} {employee?.phone || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Typography className='text-[15px] leading-[22px] text-[#FF4C51] mt-3'>
              {__(
                'Warning: Are you sure you want to delete this user? Once deleted, this user will be permanently removed from the user list.'
              )}
            </Typography>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions className='px-[20px] sm:px-[24px] text-center justify-end gap-2'>
        <Button variant='outlined' onClick={onClose} startIcon={<i className='tabler-arrow-left' />}>
          {__('Cancel')}
        </Button>
        <Button
          variant='contained'
          startIcon={<i className='tabler-trash' />}
          color='error'
          disabled={submitDeleteUserMutation.isPending}
          onClick={handleDelete}
        >
          {submitDeleteUserMutation.isPending ? __('Deleting...') : __('Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
